import { useEffect, useMemo } from 'react';
import { useStoreActions, useStoreState } from '../../../store';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useQuery } from '@tanstack/react-query';
import { partnerManagementService } from '~/services';
import { CarrierRateSelectTable } from '~/components/form/SelectCarrierRate';

// PMS caps carrierRateIds at 100. When the simulation references more than
// that, skip the server-side filter and fall back to client-side filtering.
// Proper fix is page-walking + chunking (phase 4).
const CARRIER_RATE_IDS_CAP = 100;

export interface SimulationSellRateSheetSummaryProps {
  simulationId: string;
}
export const SimulationSellRateSheetSummary = ({ simulationId }: SimulationSellRateSheetSummaryProps) => {
  const fetchSimulationSellRateMappings = useStoreActions(
    (actions) => actions.simulation.fetchSimulationSellRateMappings,
  );
  const simulationSellRateMappings = useStoreState((state) => state.simulation.simulationSellRateMappings);

  useEffect(() => {
    fetchSimulationSellRateMappings(simulationId);
  }, [fetchSimulationSellRateMappings, simulationId]);

  const sellRateIds = useMemo(
    () => (simulationSellRateMappings[simulationId] ?? []).map((v) => v.carrierRateId),
    [simulationSellRateMappings, simulationId],
  );

  const useServerSideFilter = sellRateIds.length > 0 && sellRateIds.length <= CARRIER_RATE_IDS_CAP;

  const { data: sellCarrierRates = [] } = useQuery({
    ...useQueryCreator(partnerManagementService.carrierRates.searchSummary, {
      args: [
        useServerSideFilter ? { carrierRateType: 'sell', carrierRateIds: sellRateIds } : { carrierRateType: 'sell' },
      ],
    }),
  });

  const simulationSellRateMap = useMemo(() => {
    const mappings = simulationSellRateMappings[simulationId];
    if (!mappings) return undefined;
    const map: Record<string, boolean> = {};
    for (const val of mappings) {
      map[val.carrierRateId] = true;
    }
    return map;
  }, [simulationSellRateMappings, simulationId]);

  // When server filtered, the response is already scoped — keep the client
  // filter as a no-op safety net. When falling back (over-cap), the client
  // filter is load-bearing.
  const sellRatesToShow = sellCarrierRates.filter((scr) => {
    return simulationSellRateMap && simulationSellRateMap[scr.carrierRateId];
  });

  return <CarrierRateSelectTable loading={false} carrierRates={sellRatesToShow} validate />;
};
